import { useEffect, useState } from "react";

const API_BASE = (import.meta.env.VITE_API_BASE as string) || "/api";

interface ContactInfo {
  wechatQrUrl: string;
  wechatNumber: string;
  linkUrl: string;
}

interface SimLockOverlayProps {
  userId?: number;
  addonExpired?: boolean;
  /** 返回客户 Dashboard，避免未开通客户被弹层永久卡住。 */
  onBack: () => void;
}

/**
 * 当前线上正在使用的企业微信开通界面。
 * 二维码和跳转链接继续读取管理端现有 /public/contact 配置，
 * 本次只增加返回模拟界面的能力，不改变客服配置来源。
 */
export default function SimLockOverlay({ userId, addonExpired, onBack }: SimLockOverlayProps) {
  const [contact, setContact] = useState<ContactInfo | null>(null);

  useEffect(() => {
    // 获取管理端 Dashboard 配置的企业微信二维码、微信号和跳转链接。
    const token = localStorage.getItem("alpha_token") || localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE}/public/contact`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((j) => {
        if (j.success && j.data?.list?.length > 0) {
          setContact(j.data.list[0]);
        }
      })
      .catch(() => {});
  }, []);

  const handleContact = () => {
    // 配置了企业微信链接时，在新窗口打开；没有链接时客户仍可直接扫码。
    if (contact?.linkUrl) {
      window.open(contact.linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        overflow: "auto",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 24,
          maxWidth: 400,
          width: "100%",
          padding: "40px 32px",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #fef3c7, #fde68a)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
            fontSize: 32,
          }}
        >
          🔒
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
          {addonExpired ? "模拟盘权益已到期" : "模拟盘尚未开通"}
        </h2>
        <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 20 }}>
          {addonExpired ? "您的模拟盘权益已到期，请联系客服续期，续期后即可继续使用。" : "已提交投资偏好后，请联系客服完成审核开通。"}
          <br />
          开通后即可免费使用 AI 选股、AI Trader、模拟盘等全部功能。
        </p>

        {/* 管理端 Dashboard 配置的企业微信二维码 */}
        {contact?.wechatQrUrl && (
          <div style={{ marginBottom: 16 }}>
            <img
              src={contact.wechatQrUrl}
              alt="客服二维码"
              style={{ width: 150, height: 150, borderRadius: 12, border: "1px solid #e2e8f0", objectFit: "contain", display: "block", margin: "0 auto" }}
            />
            <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>微信扫码联系客服</p>
          </div>
        )}

        {/* 联系客服开通 */}
        <button
          onClick={handleContact}
          style={{
            display: "block",
            width: "100%",
            padding: "14px 24px",
            borderRadius: 12,
            border: "none",
            background: "#07c160",
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: 14,
          }}
        >
          联系客服开通
        </button>

        <button
          type="button"
          onClick={onBack}
          style={{
            display: "block",
            width: "100%",
            padding: "12px 24px",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "#64748b",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            marginBottom: 14,
          }}
        >
          返回模拟界面
        </button>

        <p style={{ fontSize: 12, color: "#94a3b8" }}>
          开通后刷新页面生效
        </p>
        {userId ? (
          <div style={{ marginTop: 12, padding: "10px 16px", background: "#fefce8", borderRadius: 10, border: "1px solid #fde68a" }}>
            <p style={{ fontSize: 11, color: "#92400e", marginBottom: 4 }}>将此 ID 发给客服，可快速开通</p>
            <p style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", fontFamily: "monospace" }}>{userId}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
